import { SheetHeader } from "@/design-system/BottomSheet/SheetHeader";
import { Button } from "@/design-system/Button/Button";
import { CheckBox } from "@/design-system/Check/Check";
import { InfoTip } from "@/design-system/InfoTip/InfoTip";
import { TextButton } from "@/design-system/TextButton/TextButton";
import { cn } from "@/lib/utils";
import {
  OBJECTIVE_FILTER_MODES,
  fetchObjectiveGroups,
  getObjectivesSelector,
} from "@/redux/objectives/objectivesSlice";
import { AppDispatch } from "@/redux/store";
import { useTranslation } from "@repo/shared/i18n";
import { Minus, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const ONES_WE_WATCHED = "Ones We Watched";

// Display only: friendlies can be online or couch play, so it gets no label.
const MODE_ONLINE: Record<
  (typeof OBJECTIVE_FILTER_MODES)[number],
  boolean | undefined
> = {
  "squad-battles": false,
  rivals: true,
  champions: true,
  rush: true,
  "co-op": true,
  friendlies: undefined,
  draft: true,
};

const STEP_BUTTON =
  "flex size-7 items-center justify-center rounded-sm text-neutral-200 transition-colors hover:bg-neutral-700 disabled:opacity-30 disabled:hover:bg-transparent";

/** A − value + counter; `done` turns it green. */
export const Stepper = ({
  value,
  onChange,
  canDecrease,
  canIncrease,
  done = false,
}: {
  value: number;
  onChange: (value: number) => void;
  canDecrease: boolean;
  canIncrease: boolean;
  done?: boolean;
}) => {
  const { t } = useTranslation("objectives");
  return (
    <span
      className={cn(
        "flex h-8 items-center justify-center gap-0.5 rounded-md border border-neutral-600 bg-neutral-900 px-0.5 text-sm font-extrabold leading-none text-white",
        done && "border-streamline-success text-streamline-success",
      )}
    >
      <button
        type="button"
        className={STEP_BUTTON}
        disabled={!canDecrease}
        aria-label={t("plan.progress.remove")}
        onClick={() => onChange(value - 1)}
      >
        <Minus className="size-4" />
      </button>
      <span className="min-w-6 text-center">{value}</span>
      <button
        type="button"
        className={STEP_BUTTON}
        disabled={!canIncrease}
        aria-label={t("plan.progress.add")}
        onClick={() => onChange(value + 1)}
      >
        <Plus className="size-4" />
      </button>
    </span>
  );
};

export type PlanSettings = {
  /** Plain modes and `live-event:<id>` keys the user doesn't play. */
  excludedModes: string[];
};

/**
 * Customize's plan settings. Edits a draft; Apply rebuilds the bundle with it,
 * and closing without applying drops it.
 */
export const PlanSettingsPanel = ({
  compact,
  close,
  settings,
  onApply,
}: {
  compact: boolean;
  close: () => void;
  settings: PlanSettings;
  onApply: (settings: PlanSettings) => void;
}) => {
  const { t } = useTranslation(["objectives", "common"]);
  const dispatch = useDispatch<AppDispatch>();
  // The live events come with the objectives list, which the plan page may not have loaded.
  const { groups } = useSelector(getObjectivesSelector);
  useEffect(() => {
    if (groups.status === "idle") dispatch(fetchObjectiveGroups());
  }, [dispatch, groups.status]);
  const [draft, setDraft] = useState(settings);
  const setExcluded = (keys: string[]) =>
    setDraft({ ...draft, excludedModes: [...new Set(keys)] });
  // The Ones We Watched difficulty ladder is one toggle; each row can stand for several keys.
  const oww = groups.liveEvents.filter((e) =>
    e.name.startsWith(ONES_WE_WATCHED),
  );
  const modeSections: {
    title: string;
    rows: { keys: string[]; label: string; online?: boolean }[];
  }[] = [
    {
      title: t("plan.modes.title"),
      rows: OBJECTIVE_FILTER_MODES.map((mode) => ({
        keys: [mode],
        label: t(`mode.${mode}`),
        online: MODE_ONLINE[mode],
      })),
    },
    {
      title: t("plan.modes.liveEvents"),
      rows: [
        ...(oww.length > 0
          ? [
              {
                keys: oww.map((e) => `live-event:${e.id}`),
                label: ONES_WE_WATCHED,
                online: oww.every((e) => e.online === oww[0].online)
                  ? oww[0].online
                  : undefined,
              },
            ]
          : []),
        ...groups.liveEvents
          .filter((e) => !oww.includes(e))
          .map((e) => ({
            keys: [`live-event:${e.id}`],
            label: e.name,
            online: e.online,
          })),
      ],
    },
  ].filter((section) => section.rows.length > 0);
  const apply = () => {
    close();
    onApply(draft);
  };
  const applyButton = (
    <Button variant="primary" size="small" onClick={apply}>
      {t("plan.updatePlan")}
    </Button>
  );
  // Back to every mode; like any edit, it applies on Update Plan.
  const resetButton = (
    <TextButton
      disabled={draft.excludedModes.length === 0}
      onClick={() => setDraft({ ...draft, excludedModes: [] })}
    >
      {t("common:reset")}
    </TextButton>
  );

  return (
    <div className="flex flex-col text-neutral-100">
      {compact && (
        <SheetHeader
          title={t("plan.customize")}
          onClose={close}
          closeLabel={t("common:close")}
          action={
            <>
              {resetButton}
              {applyButton}
            </>
          }
        />
      )}
      {!compact && (
        // Right padding keeps the text clear of the popover's pinned close button.
        <h2 className="pl-4 pr-12 pt-4 text-[15px] font-semibold leading-[1.3] text-foreground">
          {t("plan.customize")}
        </h2>
      )}
      <div className="flex flex-col gap-y-4 p-4">
        {modeSections.map(({ title, rows }, i) => {
          const keys = rows.flatMap((row) => row.keys);
          return (
            <section key={title} className="flex flex-col gap-y-1">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-x-1.5 text-xs text-foreground-muted">
                  {title}
                  {i === 0 && (
                    <InfoTip label={title}>{t("plan.modes.info")}</InfoTip>
                  )}
                </span>
                <span className="flex items-center gap-x-3">
                  <TextButton
                    onClick={() =>
                      setExcluded(
                        draft.excludedModes.filter((k) => !keys.includes(k)),
                      )
                    }
                  >
                    {t("plan.modes.all")}
                  </TextButton>
                  <TextButton
                    onClick={() =>
                      setExcluded([...draft.excludedModes, ...keys])
                    }
                  >
                    {t("plan.modes.none")}
                  </TextButton>
                </span>
              </div>
              {rows.map(({ keys: rowKeys, label, online }) => {
                const checked = rowKeys.every(
                  (k) => !draft.excludedModes.includes(k),
                );
                return (
                  <button
                    key={rowKeys.join()}
                    type="button"
                    role="checkbox"
                    aria-checked={checked}
                    onClick={() =>
                      setExcluded(
                        checked
                          ? [...draft.excludedModes, ...rowKeys]
                          : draft.excludedModes.filter(
                              (k) => !rowKeys.includes(k),
                            ),
                      )
                    }
                    className="flex items-center gap-x-2.5 rounded-sm px-1 py-1.5 text-left text-sm text-neutral-100 hover:bg-neutral-800"
                  >
                    <CheckBox
                      checked={checked}
                      className={cn(!checked && "border-neutral-600")}
                    />
                    <span className="min-w-0 flex-1 truncate">{label}</span>
                    {online !== undefined && (
                      <span className="shrink-0 text-xs text-foreground-faint">
                        {t(online ? "plan.modes.online" : "plan.modes.offline")}
                      </span>
                    )}
                  </button>
                );
              })}
            </section>
          );
        })}
      </div>
      {!compact && (
        <div className="flex items-center justify-between border-t border-neutral-700 p-4">
          {resetButton}
          {applyButton}
        </div>
      )}
    </div>
  );
};
